import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';
import clsx from 'clsx';

import { useGetShareCategoriesQuery, useGetShareSubcategoriesQuery } from 'api/main';
import { useSendInvestmentCharacteristicsMutation } from 'api/tunnelSteps';
import { tunnelSelectors, userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { isStatusOk, getCustomMinInvestment, getFundShareCategories } from 'utils/helpers';
import { useCheckInvestmentFund, useTunnelChangeHandlers } from 'utils/hooks';
import { investmentFunds } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import TunnelStep from '../common/TunnelStep';
import TunnelBox from '../common/TunnelBox';
import { publicSharesIneligibleMessage } from './eligibility/Eligibility';
import { SubmitButton, CustomTextField, InfoButton, Loader } from 'components';
import { Box, FormLabel, InputAdornment } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

// Helper function to find category for a subcategory
const findCategoryForSubcategory = (subcategoryId, categories) => {
    return categories.find(cat => cat.shareSubcategories?.some(sc => sc.id === subcategoryId));
};

const useStyles = makeStyles(theme => ({
    input: {
        maxWidth: theme.constants.tunnelInputWidth,
    },
    formLabel: {
        letterSpacing: 1.6,
        marginBottom: theme.spacing(3),
    },
}));

const InvestmentCharacteristics = props => {
    const { handleNextStep, userStep } = props;

    const classes = useStyles();
    const dispatch = useDispatch();
    const { data: shareCategories = [], isLoading: categoriesLoader } =
        useGetShareCategoriesQuery();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery();
    const [sendInvestmentCharacteristics, { isLoading: buttonLoader, error: submitError }] =
        useSendInvestmentCharacteristicsMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const isBexIV = useCheckInvestmentFund(investmentFunds.BEX_IV);
    const isClub1 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_1);
    const isClub2 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_2);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const investmentFundValue = useMemo(
        () => tunnelSubscription?.investmentFund,
        [tunnelSubscription]
    );
    const isEvergreen = useMemo(
        () => tunnelSubscription?.investmentFund?.evergreen,
        [tunnelSubscription]
    );

    const defaultInvestingSum = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.investingSum,
        [tunnelSubscription]
    );

    const [investingSum, setInvestingSum] = useState(defaultInvestingSum);

    const subscriptionShareSubcategory = useMemo(() => {
        const subscriptionSubcategoryId =
            tunnelSubscription?.investmentCharacteristics?.shareSubcategory?.id;
        return shareSubcategories.find(sc => sc.id === subscriptionSubcategoryId) || null;
    }, [tunnelSubscription, shareSubcategories]);

    const filteredCategories = useMemo(
        () => getFundShareCategories(investmentFundValue.id, shareCategories).map(c => c.id),
        [shareCategories, investmentFundValue]
    );

    const productSubcategories = useMemo(
        () =>
            shareSubcategories.filter(sc => {
                const category = findCategoryForSubcategory(sc.id, shareCategories);
                return category && filteredCategories.includes(category.id);
            }),
        [shareSubcategories, filteredCategories, shareCategories]
    );

    const subcategory = useMemo(() => {
        const subscriptionSubcategoryId =
            tunnelSubscription?.investmentCharacteristics?.shareSubcategory?.id;
        return productSubcategories?.find(sub => sub.id === subscriptionSubcategoryId) || null;
    }, [tunnelSubscription, productSubcategories]);

    const admissionFee = useMemo(
        () => tunnelSubscription.investmentCharacteristics?.admissionFee,
        [tunnelSubscription]
    );
    const investorQualification = useMemo(
        () => tunnelSubscription.investmentCharacteristics?.investorQualification,
        [tunnelSubscription]
    );

    const investmentRange = useMemo(() => {
        let minimumInvestmentSum = subscriptionShareSubcategory?.minInvestedAmount || 0;
        if (!tunnelSubscription.referralCreated) {
            // apply absolute min (min of all product subcategories)
            minimumInvestmentSum = getCustomMinInvestment(
                investorQualification,
                productSubcategories
            );
        } // ELSE -> apply min and max from subcategory

        return {
            min: minimumInvestmentSum || 0,
            max: subscriptionShareSubcategory?.maxInvestedAmount || 0,
        };
    }, [
        subscriptionShareSubcategory,
        tunnelSubscription,
        productSubcategories,
        investorQualification,
    ]);

    const sumSchema = useMemo(
        () =>
            isBexIV || isClub1 || isClub2
                ? Joi.number()
                      .min(tunnelSubscription.referralCreated ? 0 : investmentRange.min)
                      .required()
                      .messages({
                          'number.base': texts.VEUILLEZ_SAISIR_LE_MONTANT,
                          'number.min': texts.MONANT_SUPERIEUR(investmentRange.min),
                      })
                : Joi.any(),
        [isBexIV, isClub1, isClub2, investmentRange, tunnelSubscription]
    );
    const [sumError, setSumError] = useState('');

    const handleChangeSum = useCallback(({ target: { value } }) => {
        setInvestingSum(+value);
    }, []);

    const allowSubmit = useMemo(() => !!investingSum, [investingSum]);

    const firstInstalmentPercentage = useMemo(
        () => investmentFundValue?.firstInstalmentPercentage,
        [investmentFundValue]
    );
    const firstInstalmentDelay = useMemo(
        () => investmentFundValue?.firstInstalmentDelay,
        [investmentFundValue]
    );

    const data = useMemo(() => {
        if (subcategory) {
            const partValue = isEvergreen ? '' : subcategory.value;
            const fundUnits = isEvergreen ? '' : investingSum / partValue;
            const subscriptionFee = subcategory.subscriptionFee;
            const premiumAmount = (investingSum * subscriptionFee) / 100;
            const entryFees = (investingSum * admissionFee) / 100;
            const firstInstalmentAmount =
                (firstInstalmentPercentage * investingSum) / 100 + entryFees;
            const subscriptionAmount = investingSum + premiumAmount + entryFees;
            const fundId = investmentFundValue?.id;
            return {
                partValue,
                fundUnits,
                subscriptionFee,
                premiumAmount,
                entryFees,
                firstInstalmentAmount,
                subscriptionAmount,
                fundId,
            };
        }
        return {
            partValue: isEvergreen ? '' : 0,
            fundUnits: isEvergreen ? '' : 0,
            subscriptionFee: 0,
            premiumAmount: 0,
            entryFees: 0,
            firstInstalmentAmount: 0,
            subscriptionAmount: 0,
            fundId: 0,
        };
    }, [
        admissionFee,
        subcategory,
        firstInstalmentPercentage,
        investingSum,
        investmentFundValue,
        isEvergreen,
    ]);

    const shareCategoryIneligibleMessage = useMemo(
        () =>
            publicSharesIneligibleMessage({
                ...subscriptionShareSubcategory,
                minInvestedAmount: investmentRange.min,
            }),
        [subscriptionShareSubcategory, investmentRange]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            const { error } = sumSchema.validate(investingSum);
            if (!error) {
                setSumError('');
                sendInvestmentCharacteristics({
                    investmentCharacteristics: { investingSum },
                    callback: afterSubmitCallback || (() => handleNextStep(true)),
                });
            } else {
                dispatch(
                    ineligibleSet({
                        open: true,
                        message: texts.VOTRE_RESPONSE_NE_SEMBLENT,
                    })
                );
                setSumError(error.details[0].message);
            }
        },
        [dispatch, sendInvestmentCharacteristics, sumSchema, investingSum, handleNextStep]
    );

    // returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(
        () => defaultInvestingSum !== investingSum,
        [investingSum, defaultInvestingSum]
    );

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    // Handle business logic errors in side effect (400 status errors)
    useEffect(() => {
        if (submitError?.status === 400) {
            dispatch(
                ineligibleSet({
                    open: true,
                    abandon: true,
                    message: shareCategoryIneligibleMessage,
                    title: texts.VEUILLEZ_CHOISIR_UNE_OPTION,
                })
            );
            if (investingSum < investmentRange.min) {
                setSumError(texts.SUM_INFERIOR(investmentRange.min));
            } else if (investingSum > subscriptionShareSubcategory.maxInvestedAmount) {
                setSumError(texts.SUM_SUPERIOR(subscriptionShareSubcategory.maxInvestedAmount));
            }
        }
    }, [
        submitError,
        dispatch,
        shareCategoryIneligibleMessage,
        investingSum,
        investmentRange.min,
        subscriptionShareSubcategory,
    ]);

    return (
        <TunnelStep
            title={texts.INVESTEMENT_CHARACTERISTICS}
            subtitle={texts.INVESTEMENT_CHARACTERISTICS_SUBTITLE}
        >
            <Loader loading={categoriesLoader || subcategoriesLoader}>
                <TunnelBox>
                    <Box>
                        <FormLabel
                            className={clsx(classes.formLabel, classes.extraMargin)}
                            component="legend"
                        >
                            {texts.TYPE_DE_PART} <strong>{subcategory?.name || ''}</strong>
                        </FormLabel>
                        <CustomTextField
                            disabled={!isBexIV}
                            formatNumber
                            className={classes.input}
                            label={texts.MONTANT_SOUSCRIPTION}
                            value={investingSum}
                            onChange={handleChangeSum}
                            error={sumError}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        {!currentInvestmentFund?.evergreen && (
                            <React.Fragment>
                                <CustomTextField
                                    label={texts.VALEUR_PART}
                                    className={classes.input}
                                    formatNumber
                                    disabled
                                    value={data.partValue}
                                    endAdornment={
                                        <InputAdornment position="start">€</InputAdornment>
                                    }
                                />
                                <CustomTextField
                                    label={texts.NOMBRE_PARTS_FOND}
                                    className={classes.input}
                                    disabled
                                    formatNumber
                                    value={data.fundUnits}
                                />
                            </React.Fragment>
                        )}
                        <CustomTextField
                            label={texts.COMMISION_SOUSCRIPTION}
                            className={classes.input}
                            formatNumber
                            disabled
                            value={data.subscriptionFee}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                        <CustomTextField
                            label={texts.SUBSCRIPTION_COMMISSION_VALUE}
                            disabled
                            formatNumber
                            className={classes.input}
                            value={data.premiumAmount}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        <CustomTextField
                            label={texts.ADMISSION_FEE}
                            formatNumber
                            disabled
                            className={classes.input}
                            value={admissionFee}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                        <CustomTextField
                            label={texts.MONTANT_DE_DROITS_DE_ENTREE}
                            formatNumber
                            disabled
                            className={classes.input}
                            value={data.entryFees}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        <CustomTextField
                            label={texts.MONTANT_D_ENGAGEMENT}
                            formatNumber
                            disabled
                            className={classes.input}
                            value={data.subscriptionAmount}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        {!!firstInstalmentPercentage && (
                            <Box display="flex" position="relative" alignItems="flex-end">
                                <CustomTextField
                                    label={texts.INITIAL_SLICE_AMOUNT}
                                    fullWidth
                                    disabled
                                    formatNumber
                                    className={classes.input}
                                    value={data.firstInstalmentAmount}
                                    endAdornment={
                                        <InputAdornment position="start">€</InputAdornment>
                                    }
                                    infoButton={
                                        <InfoButton
                                            disableMarginBottom
                                            title={texts.INITIAL_SLICE_AMOUNT_TITLE}
                                            content={texts.INITIAL_SLICE_AMOUNT_INFO(
                                                firstInstalmentPercentage,
                                                firstInstalmentDelay
                                            )}
                                        />
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </TunnelBox>
                <SubmitButton
                    text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                    disabled={!allowSubmit || buttonLoader}
                    onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                    loading={buttonLoader}
                />
            </Loader>
        </TunnelStep>
    );
};

InvestmentCharacteristics.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default InvestmentCharacteristics;
